import {
  AdvanceExternalPaymentVerificationResultTypenames,
  AdvanceQualificationMethod,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { mapLendingPaymentSourceToString } from 'features/advance/helpers/Advance.helpers';
import useAutoPaymentAuthorization from 'features/advance/hooks/autoPaymentAuthorization/useAutoPaymentAuthorization';
import { useExternalPaymentVerification } from 'features/advance/hooks/useExternalPaymentVerification';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

const AutopaymentAuthorization = () => {
  const router = useRouter();
  const [isChecked, setIsChecked] = useState(false);

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'autopaymentAuthorizationStrings',
  });
  const { bottom } = useSafeAreaInsets();
  const {
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate: autoPaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    qualificationMethod,
  } = useLocalSearchParams<{
    dueDate: string;
    feeAmount: string;
    amountId: string;
    advanceAmount: string;
    repaymentDate: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName: string;
    autoPaymentAgreementId?: string;
    qualificationMethod: AdvanceQualificationMethod;
  }>();

  const {
    externalPaymentVerification,
    institutionName,
    institutionLastFour,
    loading: fetchingExternalPayment,
    error: externalPaymentError,
  } = useExternalPaymentVerification({
    amount: advanceAmount,
    optionId: amountId,
  });

  const totalRepaymentAmountWithFee = (
    Number(advanceAmount) + Number(feeAmount)
  ).toFixed(2);

  const {
    data: autoPaymentAgreements,
    error: autoPaymentsAgreementsError,
    fetching: autoPaymentsAgreementsFetching,
  } = useAutoPaymentAuthorization({
    paymentSource,
    totalRepaymentAmountWithFee,
    selectedAccountId,
    autoPaymentDate,
  });

  const agreementIds =
    autoPaymentAgreements?.map((agreement) => agreement.id).join(',') || '';

  const urlParams = useMemo(
    () => ({
      advanceAmount,
      dueDate,
      feeAmount,
      amountId,
      repaymentDate: autoPaymentDate,
      selectedAccountId,
      paymentSource,
      selectedAccountName,
      autoPaymentAgreementId: agreementIds,
      qualificationMethod,
    }),
    [
      advanceAmount,
      dueDate,
      feeAmount,
      amountId,
      autoPaymentDate,
      selectedAccountId,
      paymentSource,
      selectedAccountName,
      agreementIds,
      qualificationMethod,
    ],
  );

  const paramsIncluded = !!(
    advanceAmount &&
    dueDate &&
    feeAmount &&
    amountId &&
    autoPaymentDate &&
    selectedAccountId &&
    paymentSource &&
    selectedAccountName
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-advance/autopayment-authorization',
  });

  const handleContinuePress = () => {
    if (externalPaymentVerification && autoPaymentAgreements) {
      switch (externalPaymentVerification?.__typename) {
        case AdvanceExternalPaymentVerificationResultTypenames.AdvanceExternalPaymentVerificationSuccess:
          router.navigate({
            pathname: '/varo-advance/review',
            params: urlParams,
          });
          break;
        case AdvanceExternalPaymentVerificationResultTypenames.AdvanceExternalPaymentVerificationManageCard:
          router.navigate({
            pathname: '/varo-advance/expired-card',
            params: urlParams,
          });
          break;
        case AdvanceExternalPaymentVerificationResultTypenames.AdvanceExternalPaymentVerificationLinkCard:
          router.navigate({
            pathname: '/varo-advance/link-debit-card',
            params: {
              ...urlParams,
              bankName: institutionName,
              last4: institutionLastFour,
            },
          });
          break;
        default:
          router.navigate({
            pathname: '/varo-advance/error',
          });
          break;
      }
    }
  };

  const handleBackPress = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/varo-advance/new');
    }
  };

  const isLoading = fetchingExternalPayment || autoPaymentsAgreementsFetching;
  const hasError = externalPaymentError || autoPaymentsAgreementsError;

  if (!paramsIncluded) {
    // if this is the true, it's likely the user came from a bookmarked link
    // and customer has not selected an amount yet, or we lost local state.
    // we need to figure out their status and send them to the right place. (likely select amount screen)
    <Redirect href="/varo-advance" />;
  }

  return (
    <View style={[styles.screen, { paddingBottom: bottom + theme.spacing.m }]}>
      <ScrollView contentContainerStyle={styles.scrollContentContainer}>
        <Stack.Screen
          options={{
            headerShown: true,
            headerBackVisible: true,

            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} primaryOnPress={handleBackPress} />
            ),
          }}
        />

        <ScreenTransitionOverlay
          isLoading={isLoading}
          LoadingComponent={
            <LoadingSpinner accessibilityLabel="loading indicator auto-payment" />
          }
          ErrorComponent={
            hasError ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={translatedText('error.title')}
                messageBody={translatedText('error.body')}
                primaryButton={{
                  title: translatedText('error.button'),
                  onPress: handleBackPress,
                }}
              />
            ) : null
          }
        />

        <View style={styles.agreementContainer}>
          <TitleLgText style={styles.title}>
            {translatedText('title')}
          </TitleLgText>
          <View style={styles.flexGrow}>
            {autoPaymentAgreements?.map((agreement) => (
              <BodyBaseText
                numberOfLines={0}
                key={agreement.id}
                style={styles.flexGrow}
              >
                <HTMLRenderer
                  htmlAsString={agreement.resource?.textDocumentHtml ?? ''}
                  tagsStyles={agreementContent}
                />
              </BodyBaseText>
            ))}
          </View>
        </View>

        <View>
          <View style={[styles.termsAndCondition, styles.verticalPadding]}>
            <Checkbox
              checked={isChecked}
              onSelectedStateChange={() => {
                setIsChecked(!isChecked);
              }}
              analyticsId="autopayAuthorizationCheckboxClicked"
              customAttributes={{
                autopay_authorization: !isChecked,
                autopay_method: paymentSource
                  ? mapLendingPaymentSourceToString(paymentSource)
                  : 'undefined',
              }}
              ariaLabel="debit agreement checkbox"
            />

            <View style={styles.termsAndConditionContent}>
              <BodyBaseText numberOfLines={0}>
                {translatedText('agreement', {
                  paymentMethod: selectedAccountName,
                })}
              </BodyBaseText>
            </View>
          </View>

          <View style={styles.buttonWrapper}>
            <Button
              size="LARGE"
              onPress={handleContinuePress}
              isLoading={fetchingExternalPayment}
              isDisabled={
                !isChecked ||
                (!externalPaymentVerification && !autoPaymentAgreements) ||
                isLoading
              }
              label={translatedText('button')}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const agreementContent = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  p: {
    color: theme.color.text.primary,
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
    fontSize: theme.spacing.m,
    // The way HTML Renderer calculates the styles, we have
    // to inverse the letter spacing to achieve the desired design.
    letterSpacing: -0.32,
    lineHeight: theme.spacing.l,
    margin: 0,
  },
});

const styles = StyleSheet.create({
  agreementContainer: {
    flex: 1,
  },
  buttonWrapper: {
    marginTop: theme.spacing.m,
  },
  flexGrow: {
    flexGrow: 1,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContentContainer: {
    paddingHorizontal: theme.spacing.m,
    flexGrow: 1,
  },
  termsAndCondition: {
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  termsAndConditionContent: {
    flexShrink: 1,
    paddingTop: theme.spacing.s,
  },
  title: {
    marginBottom: theme.spacing.m,
  },
  verticalPadding: {
    paddingVertical: theme.spacing.m,
  },
});

export default AutopaymentAuthorization;
